<template>
  <div class="order-panel">
    <!-- Header -->
    <div class="order-panel__header">
      <h6 v-if="hasMember" class="mb-0">Order for {{ memberName }}</h6>
      <h6 v-else class="mb-0">Current Order</h6>
    </div>

    <!-- Scrollable Line Items -->
    <div class="order-panel__items">
      <div v-if="orderItems.length === 0" class="text-center text-muted py-5">
        <i class="fas fa-shopping-basket fa-2x mb-3 d-block" style="opacity: 0.3;"></i>
        No items yet — tap products to add
      </div>
      <order-line-item
        v-for="item in orderItems"
        :key="item.product_id"
        :item="item"
        @update-quantity="onUpdateQuantity"
        @remove-item="onRemoveItem"
      />
    </div>

    <!-- Fixed Footer -->
    <div class="order-panel__footer">
      <payment-method-selector
        v-if="orderItems.length > 0"
        :value="{ method: paymentMethod, reference: paymentReference || '' }"
        :disabled="posSubmitting"
        @input="onPaymentChange"
      />
      <div class="order-panel__total">
        <span class="order-panel__total-label">Total</span>
        <span class="order-panel__total-amount">{{ formattedTotal }}</span>
      </div>
      <b-button
        variant="success"
        block
        class="order-panel__confirm-btn"
        :disabled="orderItems.length === 0 || posSubmitting"
        @click="onConfirmOrder"
      >
        <b-spinner v-if="posSubmitting" small class="mr-2" />
        {{ posSubmitting ? 'Confirming...' : 'Confirm Order' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import OrderLineItem from "@/components/pos/orderLineItem";
import PaymentMethodSelector from "@/components/pos/paymentMethodSelector";
import { posComputed, posMethods } from "@/state/helpers";

export default {
  name: "orderPanel",
  components: { OrderLineItem, PaymentMethodSelector },
  computed: {
    ...posComputed,
    formattedTotal() {
      return `R${this.orderTotal.toFixed(2)}`;
    },
  },
  methods: {
    ...posMethods,
    onUpdateQuantity({ product_id, quantity }) {
      this.updateQuantity({ product_id, quantity });
    },
    onRemoveItem(product_id) {
      this.removeItem(product_id);
    },
    onPaymentChange({ method, reference }) {
      this.setPaymentMethod(method);
      this.setPaymentReference(reference);
    },
    onConfirmOrder() {
      this.$emit("confirm-order");
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";
@import "~@/design/tokens/feedback";

.order-panel {
  display: flex;
  flex-direction: column;
  background: $surface-card;
  border: 1px solid $border-default;
  border-radius: 8px;
  height: 100%;
  min-height: 400px;

  &__header {
    padding: $space-4;
    border-bottom: 1px solid $border-default;
    color: $text-primary;
  }

  &__items {
    flex: 1;
    overflow-y: auto;
    padding: 0 $space-4;
  }

  &__footer {
    border-top: 1px solid $border-default;
    padding: $space-4;
  }

  &__total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $space-3;
  }

  &__total-label {
    font-size: $type-h2;
    color: $text-secondary;
  }

  &__total-amount {
    font-size: $type-display;
    font-weight: 700;
    color: $text-primary;
  }

  &__confirm-btn {
    min-height: 48px;
    font-size: $type-body-lg;
    font-weight: 600;
    background-color: $feedback-success;
    border-color: $feedback-success;

    &:hover:not(:disabled) {
      background-color: darken($feedback-success, 8%);
      border-color: darken($feedback-success, 8%);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
</style>
